import api from '../api'; // Adjust the import path as needed
import {
  ICreateSubjectRequestBody,
  ICreateSubjectResponseBody,
  ISubjectsResponse,
  IUpdateSubjectResponseBody,
  SubjectSearchParams,
  IAssignmentResponse,
  ICreateAssignmentRequest,
  ICreateAssignmentResponseBody,
  IDeleteAssignmentResponse,
  IAssignmentSearchParams,
  IDeleteSubjectResponse,
  ISubjectStatusResponse,
} from '../../interface/subjectModel';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { ITeacherSubjectRequestBody } from '../../interface/teacherModel';

// =====================================================
// SUBJECT CRUD SERVICE LAYER - Pure Axios Calls Only
// =====================================================

/**
 * Fetch all subjects with optional filters (Master Subjects - v2 endpoint)
 * @param params - Search parameters (page, pageSize, searchtext, status)
 * @returns Promise<ISubjectsResponse>
 */
export const fetchSubjects = async (params?: SubjectSearchParams): Promise<ISubjectsResponse> => {
  const response = await api.get<ISubjectsResponse>('/subject', { params });
  return response.data;
};

/**
 * Get single subject by ID (Master Subject - v2 endpoint)
 * @param id - Subject ID
 * @returns Promise<ISubjectsResponse>
 */
export const getSubjectById = async (id: string): Promise<ISubjectsResponse> => {
  const response = await api.get<ISubjectsResponse>(`/subject/${id}`);
  return response.data;
};

/**
 * Create a new subject (Master Subject - v2 endpoint)
 * @param subjectData - Subject data to create
 * @returns Promise<ICreateSubjectResponseBody>
 */
export const createSubject = async (
  subjectData: ICreateSubjectRequestBody,
): Promise<ICreateSubjectResponseBody> => {
  const response = await api.post<ICreateSubjectResponseBody>('/subject', subjectData);
  return response.data;
};

/**
 * Update existing subject (Master Subject - v2 endpoint)
 * @param id - Subject ID to update
 * @param subjectData - Updated subject data
 * @returns Promise<IUpdateSubjectResponseBody>
 */
export const updateSubject = async (
  id: string,
  subjectData: ICreateSubjectRequestBody,
): Promise<IUpdateSubjectResponseBody> => {
  const response = await api.put<IUpdateSubjectResponseBody>(`/subject/${id}`, subjectData);
  return response.data;
};

/**
 * Delete subject by ID (Master Subject - v2 endpoint).
 * Two-step gated delete: first call (no confirm) runs the gate-check; pass
 * `confirmed = true` to bypass the soft gate and cascade the deletion.
 * @param id - Subject ID to delete
 * @param confirmed - When true, sends `?confirmed=true` to perform the cascade
 * @returns Promise<IDeleteSubjectResponse>
 */
export const deleteSubject = async (
  id: string,
  confirmed?: boolean,
): Promise<IDeleteSubjectResponse> => {
  const response = await api.delete<IDeleteSubjectResponse>(`/subject/${id}`, {
    params: confirmed ? { confirmed: true } : undefined,
  });
  return response.data;
};

/**
 * Activate/inactivate a master subject via PATCH /v2/subject/:id/status.
 * `status: true` activates (no gate). `status: false` inactivates: active exams
 * hard-block (no bypass); linked deps soft-gate until re-called with
 * `confirmed: true`. The soft gate / hard block reject with a structured 400
 * body (`errorCode` + impact `data`) — surfaced to the screen via the thunk.
 * @param id - Subject ID
 * @param status - New status
 * @param confirmed - When true, bypasses the soft gate
 * @returns Promise<ISubjectStatusResponse>
 */
export const setSubjectStatus = async (
  id: string,
  status: boolean,
  confirmed?: boolean,
): Promise<ISubjectStatusResponse> => {
  const response = await api.patch<ISubjectStatusResponse>(`/subject/${id}/status`, {
    status,
    confirmed,
  });
  return response.data;
};

/**
 * Fetch subjects (alternative endpoint for teacher subject management)
 * @param params - Optional teacher subject request body
 * @returns Promise<ISubjectsResponse>
 */
export const fetchSubject = async (
  params: ITeacherSubjectRequestBody | void,
): Promise<ISubjectsResponse> => {
  const response = await api.get<ISubjectsResponse>('/subject', { params });
  return response.data;
};

// =====================================================
// SUBJECT ASSIGNMENT SERVICE LAYER - Pure Axios Calls Only
// =====================================================

/**
 * Fetch all subject assignments with optional pagination/filters
 * Backend endpoint: GET /v2/subject/assign
 * Returns flattened subject assignment records (one row per subject-grade-curriculum combination)
 * @param params - Search parameters (page, pageSize, searchtext, gradeId, curriculumId)
 * @returns Promise<IAssignmentResponse>
 */
export const fetchAssignments = async (
  params?: IAssignmentSearchParams,
): Promise<IAssignmentResponse> => {
  const response = await api.get<IAssignmentResponse>('/subject/assign', { params });
  return response.data;
};

/**
 * Create a new subject assignment
 * Assigns a subject to multiple grades and curriculums
 * @param assignmentData - Assignment data to create
 * @returns Promise<ICreateAssignmentResponseBody>
 */
export const createAssignment = async (
  assignmentData: ICreateAssignmentRequest,
): Promise<ICreateAssignmentResponseBody> => {
  const response = await api.post<ICreateAssignmentResponseBody>('/subject/assign', assignmentData);
  return response.data;
};

/**
 * Update existing subject assignment
 * @param id - Assignment ID to update
 * @param assignmentData - Updated assignment data
 * @returns Promise<ICreateAssignmentResponseBody>
 */
export const updateAssignment = async (
  id: string,
  assignmentData: ICreateAssignmentRequest,
): Promise<ICreateAssignmentResponseBody> => {
  const response = await api.put<ICreateAssignmentResponseBody>(
    `/subject/assign/${id}`,
    assignmentData,
  );
  return response.data;
};

/**
 * Delete subject assignment by ID
 * @param id - Assignment ID to delete
 * @returns Promise<IDeleteAssignmentResponse>
 */
export const deleteAssignment = async (
  id: string,
  confirmed?: boolean,
): Promise<IDeleteAssignmentResponse> => {
  const response = await api.delete<IDeleteAssignmentResponse>(`/subject/assign/${id}`, {
    params: confirmed ? { confirmed: true } : undefined,
  });
  return response.data;
};

// =====================================================
// REPORT SERVICE - Keep exactly as is (no changes)
// =====================================================

const getReport = async () => {
  const response = await api.get(`/subject/download`, { responseType: 'arraybuffer' });
  handleDownload(response as AxiosResponse<ArrayBuffer>);
  return response.data;
};

export const useGetReport = () => {
  const queryClient = useQueryClient();
  return useMutation(() => getReport(), {
    onSuccess: () => {
      queryClient.invalidateQueries('classRoom'); // Invalidate subjects query to refetch after deleting
    },
  });
};

const handleDownload = (response: AxiosResponse<ArrayBuffer>): void => {
  // Create an object URL from the response data (which is an ArrayBuffer)
  const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })); // Create a link element
  const link = document.createElement('a'); // Set the link's href to the object URL created from the response data
  link.href = fileUrl; // Set the filename for the download
  link.download = 'report.pdf'; // Programmatically trigger a click on the link to start the download
  link.click();
  URL.revokeObjectURL(fileUrl);
};
